import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import {useTranslation} from 'react-i18next';
import {TscStyles, TscThemeName, TscThemeNames} from '@techsmith/tsc-cloud-style-guide';
import withMemoizedContexts from '../../context/contextContainerHoC';
import reactionEmojis, {IEmojiData} from '../../constants/reactionEmojis';
import {useTscTheme} from '../../context/themeProvider';
import conversationApi from '../../service/conversationApi';
import {getNativeEmojiById} from '../../util/EmojiSearch';
import {reactionsStore, updateReactionsState} from '../../context/reactionsProvider';
import ReactionUIButton from './ReactionUIButton';
import {IEmojiReactionData} from '../../model/emojiReactionsModel';
import CssUtil from '../../util/CssUtil';
import useStateRef from '../../hooks/useStateRef';
import config from '../../service/config';
import Constants from '../../constants/Constants';
import {selfIdentifiedUserStore, updateSelfIdentifiedUserProviderState} from '../../context/selfIdentifiedUserProvider';
import {mediaStore} from '../../context/mediaProvider';
import {events} from '@techsmith/techsmith-smartplayer';
import {importMediaInterface} from '../../hooks/dynamicImport';

const timeComparisonPrecisionPlaces = 3;
const millisecondBase = 1000;
const backgroundColorOpacity = 0.75;
const embeddedButtonWidth = 2.25;
const embeddedButtonHeight = 2;

const ReactionsEmojiMenu = styled.div<{isEmbedded: boolean; controlsHidden: boolean; theme: TscThemeNames}>`
   display: ${props => props.isEmbedded && props.controlsHidden ? 'none' : 'flex'};
   flex-direction: ${props => props.isEmbedded ? 'column' : 'row'};
   ${props => props.isEmbedded && 'backdrop-filter: blur(4px)'};
   background-color: ${props => props.isEmbedded ? CssUtil.toRgba(TscStyles.color.ui.dusk.mediumDark, backgroundColorOpacity) : props.theme === TscThemeName.dawn ? TscStyles.color.ui.dawn.light : TscStyles.color.ui.dusk.mediumDark};
   padding: 0;
   border-radius: ${props => props.isEmbedded ? TscStyles.border.radius.lg : `${TscStyles.border.radius.md} 0 0 ${TscStyles.border.radius.md} `};
   ${props => props.isEmbedded && 'position: absolute'};
   ${props => props.isEmbedded && 'top: .5rem'};
   ${props => props.isEmbedded && 'right: .5rem'};
   ${props => props.isEmbedded && 'width: auto'};
   overflow: hidden;

   .reaction-menu-button:first-child {
      ${props => props.isEmbedded ? 'padding-top: .25rem' : 'padding-left: .75rem'};
      ${props => !props.isEmbedded && `border-radius: ${`${TscStyles.border.radius.md} 0 0 ${TscStyles.border.radius.md}`}`};
   }

   .reaction-menu-button:last-child {
      ${props => props.isEmbedded ? 'padding-bottom: .25rem' : 'padding-right: 0.75rem'};
   }
`;

export const ReactionsMenuBase: React.FC<IReactionsMenuProps & IStateMappedProps> = ({isEmbedded, conversationId, IsVideo, reactions, userIsSelfIdentified}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();
   const defaultEmojiSkin = 0;
   const [controlsHidden, setControlsHidden] = useStateRef(false);
   const [pendingEmojiData, setPendingEmojiData] = useState<IEmojiData>(null);

   const userHasAlreadyAddedReactionAtTime = (time: number, emojiData: IEmojiData): boolean => {
      const matchingReaction: IEmojiReactionData = reactions.find(reaction => reaction.Time.toFixed(timeComparisonPrecisionPlaces) === time.toFixed(timeComparisonPrecisionPlaces) && reaction.EmojiId === emojiData.id && reaction.Owner.Id === config.user.TechSmithId);
      return !!matchingReaction;
   };

   const createNewReaction = async (emojiData: IEmojiData) => {
      const mediaInterface = await importMediaInterface();
      const reactionTimeAsMillisecondInt = Math.round(mediaInterface.currentTime * millisecondBase);
      const reactionEmojiData: IEmojiReactionData = await conversationApi.createReaction(conversationId, emojiData.id, defaultEmojiSkin, reactionTimeAsMillisecondInt);
      const updatedReactions = reactions.concat([]);
      const indexToInsertReaction = updatedReactions.findIndex(reaction => reaction.CreateDate.getTime() > reactionEmojiData.CreateDate.getTime());
      if (indexToInsertReaction !== -1) {
         updatedReactions.splice(indexToInsertReaction, 0, reactionEmojiData);
      } else {
         updatedReactions.push(reactionEmojiData);
      }
      updateReactionsState({reactions: updatedReactions});
   };

   const getEmojiMenuItems = () => reactionEmojis.map((emojiData: IEmojiData, index: number) => {
      const emojiToDisplay = getNativeEmojiById(emojiData.id, emojiData.skin);
      const ariaLabel = IsVideo ? t('conversations.reactionAddReactionForVideoAriaLabel', {reactionEmoji: emojiToDisplay}) : t('conversations.reactionAddReactionForImageAriaLabel', {reactionEmoji: emojiToDisplay});
      return (
         <ReactionUIButton
            key={`reactionMenuButton-${index}`}
            isEmbedded={isEmbedded}
            isActive={false}
            className="reaction-menu-button"
            width={isEmbedded ? embeddedButtonWidth : null}
            height={isEmbedded ? embeddedButtonHeight : null}
            fontSize={1.25}
            padding="0 .5rem"
            margin="0"
            borderRadius={isEmbedded ? TscStyles.border.radius.md : null}
            hasBorder={false}
            title={emojiData.name}
            ariaLabel={ariaLabel}
            onClick={async () => {
               if (config.user.TechSmithId === Constants.nullTechSmithId) {
                  setPendingEmojiData(emojiData);
                  updateSelfIdentifiedUserProviderState({showSelfIdentifyModal: true});
                  return;
               }

               const mediaInterface = await importMediaInterface();
               if (!userHasAlreadyAddedReactionAtTime(mediaInterface.currentTime, emojiData)) {
                  void createNewReaction(emojiData);
               }
            }}
         >
            {emojiToDisplay}
         </ReactionUIButton>
      );
   });

   const onSmartPlayerControlsHidden = () => {
      setControlsHidden(true);
   };

   const onSmartPlayerControlsVisible = () => {
      setControlsHidden(false);
   };

   useEffect(() => {
      if (isEmbedded) {
         void importMediaInterface().then(mediaInterface => {
            mediaInterface.addEventListener(events.External.PlayerControlsHidden, onSmartPlayerControlsHidden);
            mediaInterface.addEventListener(events.External.PlayerControlsVisible, onSmartPlayerControlsVisible);
         });
      }
      return () => {
         if (isEmbedded) {
            void importMediaInterface().then(mediaInterface => {
               mediaInterface.removeEventListener(events.External.PlayerControlsHidden, onSmartPlayerControlsHidden);
               mediaInterface.removeEventListener(events.External.PlayerControlsVisible, onSmartPlayerControlsVisible);
            });
         }
      };
   }, []);

   useEffect(() => {
      if (userIsSelfIdentified && pendingEmojiData) {
         void createNewReaction(pendingEmojiData);
         setPendingEmojiData(null);
      }
   }, [userIsSelfIdentified]);

   return (
      conversationId && (
         <ReactionsEmojiMenu
            isEmbedded={isEmbedded}
            controlsHidden={controlsHidden}
            theme={theme}
         >
            {getEmojiMenuItems()}
         </ReactionsEmojiMenu>
      )
   );
};

export interface IReactionsMenuProps {
   isEmbedded: boolean;
   conversationId: string;
}

export interface IStateMappedProps {
   IsVideo: boolean;
   reactions: IEmojiReactionData[];
   userIsSelfIdentified: boolean;
}

const mapStatesToProps = (
   {isEmbedded, conversationId}: IReactionsMenuProps,
   {IsVideo}: Partial<IStateMappedProps>,
   {reactions}: Partial<IStateMappedProps>,
   {userIsSelfIdentified}: Partial<IStateMappedProps>
): IReactionsMenuProps & IStateMappedProps => ({isEmbedded, conversationId, IsVideo, reactions, userIsSelfIdentified});

export default withMemoizedContexts(mapStatesToProps, mediaStore, reactionsStore, selfIdentifiedUserStore)(ReactionsMenuBase);
